import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { IconType } from './Chip';

export interface RowAction {
    id: string;
    label: string;
    icon?: IconType;
    /** Navigates. Use onSelect for everything else. */
    to?: string;
    onSelect?: () => void;
    tone?: 'danger';
    /** Divider above, used before Delete. */
    separated?: boolean;
}

export interface RowActionsProps {
    actions: RowAction[];
    /** What the menu is for, e.g. the row name: "Actions for Lunch menu". */
    label: string;
}

const itemClass =
    'flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-lg px-3 text-left text-small data-[focus]:bg-hover [@media(pointer:coarse)]:min-h-11';

/** One 36 px "more" button (44 px on touch) opening View / Edit / Delete. */
export const RowActions = ({ actions, label }: RowActionsProps) => {
    const { t } = useTranslation();
    return (
        <Menu>
            <MenuButton
                aria-label={t('ui.actionsFor', { name: label })}
                className="relative inline-flex size-9 items-center justify-center rounded-control text-strong transition-colors duration-hover ease-snap hover:bg-hover after:absolute after:-inset-1 after:content-[''] [@media(pointer:coarse)]:size-11"
            >
                <EllipsisHorizontalIcon className="size-5" aria-hidden="true" />
            </MenuButton>
            <MenuItems
                anchor="bottom end"
                transition
                className="z-40 min-w-44 rounded-control bg-raised p-1 shadow-e2 outline-none transition duration-hover ease-snap [--anchor-gap:4px] data-[closed]:opacity-0"
            >
                {actions.map((a) => {
                    const Icon = a.icon;
                    const cls = clsx(itemClass, a.tone === 'danger' ? 'text-danger' : 'text-strong');
                    const inner = (
                        <>
                            {Icon && <Icon className="size-5" aria-hidden="true" />}
                            {a.label}
                        </>
                    );
                    return (
                        <Fragment key={a.id}>
                            {a.separated && <div role="separator" className="my-1 h-px bg-line" />}
                            <MenuItem>
                                {a.to ? (
                                    <Link to={a.to} className={cls}>
                                        {inner}
                                    </Link>
                                ) : (
                                    <button type="button" onClick={a.onSelect} className={cls}>
                                        {inner}
                                    </button>
                                )}
                            </MenuItem>
                        </Fragment>
                    );
                })}
            </MenuItems>
        </Menu>
    );
};
